import { createBrowserClient, createServerClient, type CookieOptions } from '@supabase/ssr'
import type { Session, SupabaseClient, PostgrestError } from '@supabase/supabase-js'
import type { User, BusinessUser } from '@/types/auth'
import type { NormalizedUser } from '../types/user-schema'
import type { BusinessStaff } from '@/types/business'
// Import and re-export the normalizeUser function for centralized user normalization
import { normalizeUser } from './normalize-user.service'
export { normalizeUser }
import axios from 'axios'

// Environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL 
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY 

// Create a default Supabase client instance for direct imports
export const supabase = createBrowserClient(supabaseUrl, supabaseAnonKey)

// Type for Supabase query response
export type SupabaseResponse<T> = {
  data: T | null;
  error: PostgrestError | null;
}

/**
 * Creates a Supabase client for browser environments
 * Uses existing session if available to maintain authentication state
 */


/**
 * Creates a Supabase client for server environments
 */
export const createServerSupabaseClient = (cookies: {
  get?: (name: string) => string | undefined
  set?: (name: string, value: string, options: CookieOptions) => void
}) => {
  return createServerClient(
    supabaseUrl,
    supabaseAnonKey,
    {
      cookies: {
        get: (name: string) => cookies.get?.(name),
        set: (name: string, value: string, options: CookieOptions) => {
          cookies.set?.(name, value, options)
        },
        remove: (name: string, options: CookieOptions) => {
          cookies.set?.(name, '', { ...options, maxAge: 0 })
        },
      },
    }
  )
}

export const createClient = async (options?: { auth?: any }) => {
  // Get site URL from environment variable with fallback to window.location.origin
  const siteUrl = import.meta.env.VITE_SITE_URL || 
    (typeof window !== 'undefined' ? window.location.origin : undefined);
  
  // Create client with auth configuration
  const client = createBrowserClient(
    supabaseUrl,
    supabaseAnonKey,
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
        // Set the site URL for auth flows like password reset
        site: siteUrl,
        // Merge with any provided options
        ...options?.auth
      }
    }
  );
  
  try {
    // Get existing session directly from Supabase
    // This will verify the JWT token with Supabase on each client creation
    const { data: { session } } = await client.auth.getSession();
    
    // Apply headers if we have a session
    if (session?.access_token) {
      // Verify the JWT token is valid by making a test request to Supabase
      const { error: authError } = await client.auth.getUser();
      
      if (authError) {
        console.error('JWT token validation failed in createClient:', authError);
        // Clear invalid session
        await client.auth.signOut();
        throw new Error('Invalid JWT token');
      }
      
      // Apply headers with valid token
      applyHeaders(session);
    }
  } catch (err) {
    console.error('Error getting session in createClient:', err);
    // Don't throw here to allow the client to be created even without a valid session
    // The AuthContext will handle authentication state
  }
  
  return client;
}

/**
 * Apply authentication headers to API clients
 */
export const applyHeaders = (session: Session | null) => {
  if (session?.access_token) {
    // Set both Authorization header and apikey header
    axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
    axios.defaults.headers.common['apikey'] = supabaseAnonKey;
    
    // Store session access token in localStorage for API calls
    localStorage.setItem('supabase_access_token', session.access_token);
    
    console.log('Applied auth headers with valid access token');
  } else {
    console.warn('Attempted to apply headers with null session or missing token');
  }
}

/**
 * Initialize Supabase authentication
 * @param sessionOrToken - Either a full Session object or an access token
 * @param refreshToken - Optional refresh token (when first param is an access token)
 * @param userData - Optional user data (when first param is an access token)
 */
export const initSupabaseAuth = async (
  sessionOrToken: Session | string | null,
  refreshToken?: string,
  userData?: any
) => {
  let session: Session | null = null;
  
  // Handle case where we're passed individual tokens instead of a session
  if (typeof sessionOrToken === 'string' && sessionOrToken) {
    // Create a session object from the tokens
    session = {
      access_token: sessionOrToken,
      refresh_token: refreshToken || '',
      user: userData || null,
      expires_at: Math.floor(Date.now() / 1000) + 3600, // Default 1 hour expiry
      expires_in: 3600,
      token_type: 'bearer'
    };
    
    // Try to set the session in the Supabase client
    try {
      const client = await createClient();
      await client.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token || ''
      });
      
      // No need to store session manually, Supabase handles this
      applyHeaders(session);
      
      // Set up session refresh
      setupSessionRefresh();
      
      return;
    } catch (error) {
      console.error('Error initializing Supabase auth with token:', error);
      return;
    }
  }
  
  // Handle case where we're passed a full session object
  if (sessionOrToken && typeof sessionOrToken === 'object') {
    session = sessionOrToken;
    
    try {
      const client = await createClient();
      await client.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token || ''
      });
      
      // No need to store session manually, Supabase handles this
      applyHeaders(session);
      
      // Set up session refresh
      setupSessionRefresh();
    } catch (error) {
      console.error('Error initializing Supabase auth with session:', error);
    }
  }
}

/**
 * Sign out from Supabase
 */
export const supabaseSignOut = async () => {
  try {
    const client = await createClient();
    await client.auth.signOut();
    
    // Supabase handles clearing the session internally
    // Just need to clear API headers
    delete axios.defaults.headers.common['Authorization'];
  } catch (error) {
    console.error('Error signing out:', error);
  }
}

/**
 * Alias for supabaseSignOut for backward compatibility
 */
export const signOut = supabaseSignOut

/**
 * Get the current session from a server-side Supabase client
 * This is used in SSR contexts like Next.js getServerSideProps
 * 
 * @param serverSupabaseClient - The server-side Supabase client created with createServerSupabaseClient
 * @returns The current session or null if not authenticated
 */
export const getSessionFromSSR = async (
  serverSupabaseClient: SupabaseClient
): Promise<Session | null> => {
  try {
    const { data, error } = await serverSupabaseClient.auth.getSession();
    
    if (error) {
      console.error('Error getting session from SSR:', error);
      return null;
    }
    
    return data.session;
  } catch (error) {
    console.error('Exception getting session from SSR:', error);
    return null;
  }
}

/**
 * Initialize Supabase authentication in a server-side rendering context
 * This should be used in SSR contexts like Next.js getServerSideProps
 * 
 * @param serverSupabaseClient - The server-side Supabase client created with createServerSupabaseClient
 * @param session - The session object from the server-side client
 */
export const initSupabaseAuthSSR = async (
  serverSupabaseClient: SupabaseClient,
  session: Session | null
): Promise<void> => {
  if (!session) {
    return;
  }
  
  try {
    // Set the session in the server-side client
    await serverSupabaseClient.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token || ''
    });
    
    // Apply headers for server-side API calls
    applyHeaders(session);
  } catch (error) {
    console.error('Error initializing Supabase auth in SSR:', error);
  }
}

/**
 * Gets the current user from a server-side Supabase client
 * This is used in SSR contexts like Next.js getServerSideProps
 * 
 * @param serverSupabaseClient - The server-side Supabase client created with createServerSupabaseClient
 * @returns The current authenticated user or null if not authenticated
 */
export const getCurrentUserFromSSR = async (
  serverSupabaseClient: SupabaseClient
): Promise<NormalizedUser | null> => {
  try {
    // Get the session from the server-side client
    const { data, error } = await serverSupabaseClient.auth.getSession();
    
    if (error || !data.session) {
      return null;
    }
    
    const user = data.session.user;
    if (!user) {
      return null;
    }
    
    // Use the centralized normalizeUser function
    const normalizedUser = normalizeUser(user);
    
    // Log user details for debugging
    console.debug('User from SSR:', {
      id: normalizedUser.id,
      email: normalizedUser.email,
      role: normalizedUser.role,
      businessStaffRole: normalizedUser.businessStaffRole,
      business_id: normalizedUser.business_id
    });
    
    // Return the normalized user
    return normalizedUser;
  } catch (error) {
    console.error('Error getting user from SSR context:', error)
    return null
  }
}

/**
 * Setup session refresh to handle token expiration
 */
export const setupSessionRefresh = async () => {
  try {
    const client = await createClient();
    
    // Get the current session directly from Supabase
    const { data } = await client.auth.getSession();
    const session = data.session;
    
    if (session && session.expires_at) {
      const expiresAt = new Date(session.expires_at * 1000);
      const now = new Date();
      
      // Refresh 5 minutes before expiration
      const refreshTime = expiresAt.getTime() - now.getTime() - (5 * 60 * 1000);
      
      // Log session expiration info for debugging
      console.log(`Session expires at ${expiresAt.toISOString()}, will refresh in ${Math.floor(refreshTime/1000/60)} minutes`);
      
      if (refreshTime > 0) {
        setTimeout(async () => {
          try {
            console.log('Attempting to refresh session...');
            const { data, error } = await client.auth.refreshSession();
            if (error) {
              console.error('Error refreshing session:', error);
              // If refresh fails, try to get a new session
              const { data: currentData } = await client.auth.getSession();
              if (currentData.session) {
                console.log('Using existing valid session instead');
                applyHeaders(currentData.session);
              }
            } else if (data.session) {
              console.log('Successfully refreshed session');
              // Apply headers with the new session token
              applyHeaders(data.session);
              
              // Set up the next refresh
              setupSessionRefresh();
            }
          } catch (e) {
            console.error('Exception refreshing session:', e);
          }
        }, refreshTime);
      } else {
        // Session already expired or about to expire, refresh now
        console.log('Session expired or about to expire, refreshing now');
        const { data: refreshData, error } = await client.auth.refreshSession();
        if (!error && refreshData.session) {
          applyHeaders(refreshData.session);
          setupSessionRefresh(); // Set up next refresh
        }
      }
    } else {
      console.warn('No valid session or expiration time found');
    }
  } catch (error) {
    console.error('Error setting up session refresh:', error);
  }
}

/**
 * Type guard for business users
 */
export const isBusinessUser = (user: User): user is BusinessUser => {
  return user.role === 'business'
}

/**
 * Check if user has charity role
 */
export const isCharityUser = (user: any): boolean => {
  return user && user.role === 'charity'
}

/**
 * Check if user is a staff member
 */
export const isStaffMember = (user: any): user is BusinessStaff=> {
  return user && user.staff_id !== undefined
}

/**
 * Check if user is a super admin
 */
export const isSuperAdmin = (user: any): boolean => {
  return user && (user.role === 'admin' || user.role === 'super_admin' || user.role === 'supabase_admin')
}

/**
 * Check if user has business or admin permissions
 */
export const hasBusinessOrAdminPermission = (user: any): boolean => {
  return user && (isBusinessUser(user) || isSuperAdmin(user) || (user.staff_id && user.role === 'business_staff'))
}

/**
 * Get the current authenticated user
 * @returns The current user or null if not authenticated
 */
export const getCurrentUser = async (): Promise<NormalizedUser | null> => {
  try {
    const client = await createClient();
    const { data: { session } } = await client.auth.getSession();
    
    if (!session) {
      return null;
    }
    
    const user = session.user;
    if (!user) {
      return null;
    }
    
    // Use the centralized normalizeUser function from our service
    return normalizeUser(user);
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

export const configureAuthListener = async () => {
  const client = await createClient();
  
  try {
    // First check if we already have a session
    const { data: sessionData } = await client.auth.getSession();
    if (sessionData.session) {
      console.log('Found existing session, applying headers');
      applyHeaders(sessionData.session);
      setupSessionRefresh();
    }
    
    // Set up auth state change listener
    const { data: { subscription } } = client.auth.onAuthStateChange(async (event, session) => {
      console.log(`Auth state changed: ${event}`, session ? 'with session' : 'without session');
      
      switch (event) {
        case 'SIGNED_IN':
          console.log('User signed in');
          await initSupabaseAuth(session);
          break;
          
        case 'SIGNED_OUT':
          console.log('User signed out');
          // Clear all auth headers and tokens
          delete axios.defaults.headers.common['Authorization'];
          delete axios.defaults.headers.common['apikey'];
          localStorage.removeItem('supabase_access_token');
          localStorage.removeItem('session');
          break;
          
        case 'TOKEN_REFRESHED':
          console.log('Token refreshed');
          if (session) {
            // Apply headers with the new session token
            applyHeaders(session);
            
            // Set up the next refresh
            setupSessionRefresh();
          }
          break;
          
        case 'PASSWORD_RECOVERY':
          console.log('Password recovery detected');
          // This is handled in the ResetPassword component
          break;
          
        case 'USER_UPDATED':
          console.log('User updated');
          if (session) {
            // Apply headers with the updated user session
            applyHeaders(session);
          }
          break;
          
        default:
          console.log(`Unhandled auth event: ${event}`);
          break;
      }
    });
    
    console.log('Supabase client auth listener configured successfully');
    return subscription;
  } catch (error) {
    console.error('Error configuring auth listener:', error);
    return null;
  }
}